import moment from 'moment';
import React, { forwardRef, useState } from 'react';
import DateRangePicker from 'react-datepicker';
import { Button, Typography } from '..';
import { CalendarIcon2 } from '../../../theme/icons2';
import { getLastNyears } from '../../../utils/dateutils';
import { useClickOutside } from '../../useOutsideClick';
import "./index.css";
import { CommonDropdown } from './MonthDropdown';

const CalendarRange = ({ start = new Date(), end = new Date(), handleChangeDate = () => null, defaultDate = {} }) => {
    const ref = React.createRef(null);
    const [isOpenCalendar, setIsOpenCalendar] = useState(false);

    const CustomInput = forwardRef(({ value, onClick, className, enableIcon = false }, ref) => (<button className={className} onClick={onClick} ref={ref}>
        {enableIcon && <CalendarIcon2 className="h-4 w-4" />}
        <Typography variant="base" text={moment(value)?.format('DD MMM YYYY')} className="font-semibold text-secondary-500" />
    </button>));

    const renderDayContents = (day, date) => {
        const tooltipText = `${moment(date)?.format('MM-DD-YYYY')}`;
        return <span className='text-base   m-1 flex justify-center items-center' title={tooltipText}>{day}</span>;
    };

    const formatWeekDay = (name) => <div className={` flex justify-center items-center bg-secondary-50`} >
        <Typography className={'text-base text-secondary-500 m-1 flex justify-center items-center'}
            variant="sm" text={name?.substring(0, 1)?.toUpperCase()} /></div>

    useClickOutside(ref, () => setIsOpenCalendar(false));

    return (
        <div className='w-fit relative' ref={ref}>
            <button onClick={() => setIsOpenCalendar(!isOpenCalendar)} className={`flex gap-2 items-center text-sm border  hover:border-secondary-400   rounded-lg px-2 py-1 ${isOpenCalendar ? 'border-primary-400' : 'border-secondary-200'}`}>
                <CalendarIcon2 className="h-4 w-4" />
                <Typography variant="base" text={`${start ? moment(start)?.format('DD MMM YYYY') : 'Select From'} - ${end ? moment(end)?.format('DD MMM YYYY') : 'Select To'}`} className="font-semibold text-secondary-500" />
            </button>
            <div className='calendar-with-date-range '>
                {isOpenCalendar && <div className='flex gap-0 flex-col lg:flex-row items-start mt-2 absolute right-0  '>
                    <DateRangePicker
                        selected={start}
                        renderDayContents={renderDayContents}
                        formatWeekDay={formatWeekDay}
                        selectsStart={true}
                        shouldCloseOnSelect={true}
                        onChange={(date) => handleChangeDate([date, null])}
                        customInput={<CustomInput enableIcon={true} className=" flex items-center gap-2 text-left text-sm  px-1 py-0 rounded-lg text-secondary-500  " />}
                        startDate={start}
                        endDate={end}
                        inline={true}
                        showPopperArrow={false}
                        calendarClassName="  calendar-with-date-range-modal min-h-[410px]"
                        renderCustomHeader={(props) => <CalendarHeader defaultDate={defaultDate} setIsOpenCalendar={setIsOpenCalendar} handleChangeDate={handleChangeDate} headerCount={0} {...props} />}
                        className="relative"
                        isClearable={true}
                    />
                    <DateRangePicker
                        renderDayContents={renderDayContents}
                        formatWeekDay={formatWeekDay}
                        showPopperArrow={false}
                        selectsEnd={true}
                        inline={true}
                        shouldCloseOnSelect={true}
                        isClearable={true}
                        onChange={(date) => { handleChangeDate([start, date]); setIsOpenCalendar(false) }}
                        customInput={<CustomInput enableIcon={false} className=" flex items-center gap-2 text-left text-sm  px-1 py-0 rounded-lg text-secondary-500  " />}
                        startDate={start}
                        endDate={end}
                        selected={end}
                        className="relative"
                        calendarClassName=" calendar-with-date-range-modal min-h-[410px]"
                        renderCustomHeader={(props) => <CalendarHeader defaultDate={defaultDate} setIsOpenCalendar={setIsOpenCalendar} handleChangeDate={handleChangeDate} headerCount={1}  {...props} />}
                        minDate={start}
                    />
                </div>}
            </div>
        </div>
    )
}

export default CalendarRange

const CalendarHeader = (props) => {
    const yearList = getLastNyears({ length: 5 })?.map((years) => ({ label: years?.year, year: years?.year }))

    const monthList = [
        { value: 0, label: "January" },
        { value: 1, label: "February" },
        { value: 2, label: "March" },
        { value: 3, label: "April" },
        { value: 4, label: "May" },
        { value: 5, label: "June" },
        { value: 6, label: "July" },
        { value: 7, label: "August" },
        { value: 8, label: "September" },
        { value: 9, label: "October" },
        { value: 10, label: "November" },
        { value: 11, label: "December" }
    ]

    const { changeYear, changeMonth, monthDate, customHeaderCount, defaultDate, setIsOpenCalendar, nextMonthButtonDisabled, nextYearButtonDisabled, prevMonthButtonDisabled, prevYearButtonDisabled, headerCount, handleChangeDate = () => null } = props;

    const handleChange = (e, type) => {
        if (type === 'month') {
            changeMonth(Number(e.target.value))
        } else {
            changeYear(Number(e.target.value))
        }
    }

    const handleClear = () => {
        handleChangeDate([new Date(defaultDate?.start), new Date(defaultDate?.end)]);
        setIsOpenCalendar(false);
    }

    return (
        <div className='bg-white '>
            <div className='flex justify-between items-center'>
                <Typography variant="base" text={`${headerCount === 0 ? 'From' : 'To'}`} className="text-left font-semibold text-primary-400  text-sm w-full" />
                {headerCount === 1 && <Button text="Reset" variant="tertiary" className='text-sm' onClick={handleClear} />}
            </div>
            <div className='flex justify-between'>
                <CommonDropdown type="month" disabled={customHeaderCount || nextMonthButtonDisabled || nextYearButtonDisabled || prevMonthButtonDisabled || prevYearButtonDisabled} value={moment(monthDate)?.month()} handleChange={handleChange} dataList={monthList} />
                <CommonDropdown type="year" disabled={customHeaderCount || nextMonthButtonDisabled || nextYearButtonDisabled || prevMonthButtonDisabled || prevYearButtonDisabled} value={moment(monthDate)?.year()} handleChange={handleChange} dataList={yearList} />
            </div>
        </div>
    )
} 